import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Image, Calendar, Briefcase, FileText, TrendingUp, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    gallery: 0,
    events: 0,
    services: 13,
    content: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [galleryRes, eventsRes, contentRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/gallery`),
        fetch(`${API_URL}/api/admin/events`),
        fetch(`${API_URL}/api/admin/content`)
      ]);

      const gallery = await galleryRes.json();
      const events = await eventsRes.json();
      const content = await contentRes.json();

      setStats({
        gallery: gallery.length || 0,
        events: events.length || 0,
        services: 13,
        content: content.length || 0
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Gallery Photos', value: stats.gallery, icon: Image, color: 'bg-blue-500', link: '/admin/gallery' },
    { title: 'Events', value: stats.events, icon: Calendar, color: 'bg-green-500', link: '/admin/events' },
    { title: 'Services', value: stats.services, icon: Briefcase, color: 'bg-purple-500', link: '/admin/services' },
    { title: 'Content Sections', value: stats.content, icon: FileText, color: 'bg-orange-500', link: '/admin/content' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-black">
          <h1 className="text-3xl font-bold mb-2">Welcome to Finest Feathers Admin</h1>
          <p className="opacity-80">Manage your website content, gallery, events, and more.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Link key={stat.title} to={stat.link}>
              <Card className="bg-zinc-900 border-zinc-800 hover:border-orange-500 transition-all cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-white">
                        {loading ? '...' : stat.value}
                      </p>
                    </div>
                    <div className={`${stat.color} p-3 rounded-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/admin/gallery"
                className="bg-zinc-800 hover:bg-zinc-700 p-4 rounded-lg flex items-center gap-3 transition-colors"
              >
                <Image className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-white font-medium">Add Gallery Photo</p>
                  <p className="text-gray-400 text-sm">Upload new images</p>
                </div>
              </Link>
              <Link
                to="/admin/events"
                className="bg-zinc-800 hover:bg-zinc-700 p-4 rounded-lg flex items-center gap-3 transition-colors"
              >
                <Calendar className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-white font-medium">Manage Events</p>
                  <p className="text-gray-400 text-sm">Add or edit events</p>
                </div>
              </Link>
              <Link
                to="/admin/content"
                className="bg-zinc-800 hover:bg-zinc-700 p-4 rounded-lg flex items-center gap-3 transition-colors"
              >
                <FileText className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-white font-medium">Edit Content</p>
                  <p className="text-gray-400 text-sm">Update page sections</p>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-gray-400 space-y-2">
              <p><strong className="text-orange-500">Gallery:</strong> Upload, reorder, and delete photos from your gallery carousel.</p>
              <p><strong className="text-orange-500">Events:</strong> Add upcoming events with registration links, dates, and images.</p>
              <p><strong className="text-orange-500">Services:</strong> Edit your service offerings with icons and descriptions.</p>
              <p><strong className="text-orange-500">Content:</strong> Update hero text, mission statement, story, and more.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
